import {
    Asset,
    Deployment,
    Job, Provider,
    Repo,
    RepoDetail,
    RepoInfo, RepoSettingsPut,
    Server,
    ServerToken,
    Skeleton,
    Task,
    User,
    WorkerLogs
} from "./types";
import {v4 as uuidv4} from "uuid";
import {useNavigate} from "react-router-dom";

class Socket {
    private socket: WebSocket | null = null;
    private reconnectInterval = 2000;
    private reconnectTimeout: NodeJS.Timeout | null = null;

    constructor() {
        this.connect();
    }

    private connect() {
        if (this.socket) {
            this.socket.close();
        }
        this.socket = new WebSocket('wss://api-ws-ci.zenzon.net');//ws://localhost:5028
        this.socket.onopen = () => {
            console.log('WebSocket connected');
            if (this.reconnectTimeout) {
                clearTimeout(this.reconnectTimeout);
                this.reconnectTimeout = null;
            }
        };
        this.socket.onmessage = (event: MessageEvent) => {
            const response = JSON.parse(event.data);
            console.log('Received message:', response);
            if (response.type && response.type !== 'subscription') {
                return;
            }
            let callback = subscribers[response.id];
            if (callback) {
                callback(response.data);
            }
        }
        this.socket.onclose = () => {
            console.log('WebSocket disconnected, reconnecting...');
            this.scheduleReconnect();
        };
        this.socket.onerror = (error) => {
            console.error('WebSocket error:', error);
            this.scheduleReconnect();
        };
    }

    private scheduleReconnect() {
        if (!this.reconnectTimeout) {
            this.reconnectTimeout = setTimeout(() => {
                this.connect();
            }, this.reconnectInterval);
        }
    }

    private async generateRoute<T>(method: 'get' | 'post' | 'put' | 'delete' | 'patch', route: string, body: any | null = null): Promise<T> {
        const id: string = uuidv4();
        const token = document.cookie.split('; ').find(row => row.startsWith('token='))?.split('=')[1] ?? null;
        const header: any = {
            id: id,
            method: method.toUpperCase(),
            route: route,
            data: body ?? {},
            headers: {}
        };
        if (token) {
            header.headers['Authorization'] = 'Bearer '+token;
        }
        return new Promise<T>(async (resolve, reject) => {
            while (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
                await new Promise<void>((resolve, reject) => {
                    setTimeout(() => {
                        resolve();
                    }, 1000);
                })
            }

            const handleMessage = async (event: MessageEvent) => {
                const response = JSON.parse(event.data);

                if (response.id !== id) {
                    return; // Ignore messages that do not match the request ID
                }

                if (response.type === 'error' || (response.status / 100).toFixed(0) !== '2') {
                    if (response.data === 'You are not logged in.' || response.status === 401) {
                        //get token from cookies
                        //window.location.href = '/login';
                        console.log('Unauthorized');
                        reject(response.data);
                        this.socket?.removeEventListener('message', handleMessage);
                        window.location.href = '/login';
                        return;
                    }else{
                        reject(response.data);
                        this.socket?.removeEventListener('message', handleMessage);
                        return;
                    }
                }

                resolve(response.data);
                this.socket?.removeEventListener('message', handleMessage);
            };

            this.socket.addEventListener('message', handleMessage);
            console.log('Sending message:', header);
            this.socket.send(JSON.stringify(header));
        })
    }

    public async get<T>(route: string, body: any | null = null): Promise<T> {
        return this.generateRoute<T>('get', route, body);
    }

    public async post<T>(route: string, body: any | null = null): Promise<T> {
        return this.generateRoute<T>('post', route, body);
    }

    public async put<T>(route: string, body: any | null = null): Promise<T> {
        return this.generateRoute<T>('put', route, body);
    }

    public async delete<T>(route: string, body: any | null = null): Promise<T> {
        return this.generateRoute<T>('delete', route, body);
    }

    public async patch<T>(route: string, body: any | null = null): Promise<T> {
        return this.generateRoute<T>('patch', route, body);
    }


}

const socket = new Socket();

let subscribers: { [id: string]: ((data: any) => void) } = {};

const subscribe = (topic: string, callback: (data: any) => void): string => {
    console.log('Subscribing route:', topic);
    let id = uuidv4();
    subscribers[id] = callback;
    socket.post<void>(`/subscribe`, {topic, id}).then();
    return id;
}

const unsubscribe = (id: string) => {
    console.log('unSubscribing route:', id);
    if (subscribers[id]) {
        delete subscribers[id];
        socket.delete<void>(`/subscribe`, {id}).then();
    }
}

const agent = {
    getJobs: () => socket.get<Job[]>('/job'),
    getJob: (id: string) => socket.get<Job>(`/job/${encodeURIComponent(id)}`),

    getDeployments: () => socket.get<Deployment[]>('/deployment'),
    startDeployment: (deploymentId: string) => socket.post<void>(`/deployment/${encodeURIComponent(deploymentId)}/start`),
    stopDeployment: (deploymentId: string) => socket.post<void>(`/deployment/${encodeURIComponent(deploymentId)}/stop`),

    getTasks: () => socket.get<Task[]>(`/task`),
    getTask: (id: string) => socket.get<Task>(`/task/${encodeURIComponent(id)}`),
    postTask: (data: Task) => socket.post<void>(`/task/`, data),
    putTask: (data: Task) => socket.put<void>(`/task/${encodeURIComponent(data.id)}`, data),
    deleteTask: (id: string) => socket.delete<void>(`/task/${encodeURIComponent(id)}`),

    getSkeleton: (id: string) => socket.get<Skeleton>(`/skeleton/${encodeURIComponent(id)}`),
    reRun: (id: string) => socket.post<WorkerLogs[]>(`/skeleton/${encodeURIComponent(id)}/rerun`),
    putConfirm: (id: string) => socket.put<WorkerLogs[]>(`/skeleton/${encodeURIComponent(id)}/confirm`),
    abortRun: (id: string) => socket.delete<WorkerLogs[]>(`/skeleton/${encodeURIComponent(id)}`),

    login: (username: string, password: string) => socket.post<{
        username: string;
        session: string;
        requirePasswordChange: boolean;
    }>(`login`, {username, password}),

    loginUpdate: (newPassword: string) => socket.post<void>(`login/update`, {newPassword}),

    encrypt: (data: string) => socket.post<{encryptedData: string}>(`/encryption/encrypt`, {data}),
    decrypt: (data: string) => socket.post<{decryptedData: string}>(`/encryption/decrypt`, {data}),

    rescan: () => socket.get<void>(`/job/rescan`),

    requestAsset: (asset: string, id: string) => socket.get<Asset>(`/skeleton/${encodeURIComponent(id)}/asset/download`, {asset}),

    setupTest: (data: any[]) => socket.post<void>(`/test/setup`, {pipeline: data}),
    runTest: (data: string[], clickedElementId: string, repo: string) => socket.post<void>(`/test/run`, {
        data,
        clickedElementId,
        repo
    }),

    getRepoInfo: (repo: string) => socket.get<RepoInfo>(`/repo/${encodeURIComponent(repo)}/info`),
    putRepoInfo: (repo: string, body: RepoSettingsPut) => socket.put<RepoInfo>(`/repo/${encodeURIComponent(repo)}`, body),

    getServers: () => socket.get<Server[]>('/server'),
    getServer: (id: string) => socket.get<Server>(`/server/${id}`),
    postServer: (server: Server) => socket.post<Server>(`/server`, server),
    putServer: (server: Server) => socket.put<Server>(`/server/${server.id}`, server),
    deleteServer: (id: string) => socket.delete<void>(`/server/${id}`),

    getUsers: () => socket.get<User[]>('/user'),
    getUser: (id: string) => socket.get<User>(`/user/${id}`),
    postUser: (user: User) => socket.post<User>(`/user`, user),
    putUser: (user: User) => socket.put<User>(`/user/${user.id}`, user),
    deleteUser: (id: string) => socket.delete<void>(`/user/${id}`),

    getTokens: (serverId: string) => socket.get<ServerToken[]>(`/server/${encodeURIComponent(serverId)}/token`),
    postToken: (serverId: string) => socket.post<ServerToken>(`/server/${encodeURIComponent(serverId)}/token`),
    deleteToken: (serverId: string, tokenId: string) => socket.delete<void>(`/server/${encodeURIComponent(serverId)}/token/${encodeURIComponent(tokenId)}`),
    putToken: (serverId: string, tokenId: string, token: ServerToken) => socket.put<void>(`/server/${encodeURIComponent(serverId)}/token/${encodeURIComponent(tokenId)}`, token),

    getRepos: () => socket.get<Repo[]>('/repo'),
    getRepo: (repo: string) => socket.get<RepoDetail>(`/repo/${encodeURIComponent(repo)}`),

    putRunRepoBranch: (repo: string, branch: string) => socket.put<void>(`/repo/${encodeURIComponent(repo)}/runbranch/${encodeURIComponent(branch)}`),

    getProviders: () => socket.get<Provider[]>('/provider'),

    subscribe: subscribe,
    unsubscribe: unsubscribe,
    deleteRepo: (repo: string) => socket.delete<void>(`/repo/${repo}`),
}

export {agent};